import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, GraduationCap, Search, Edit2, Trash2 } from 'lucide-react';
import { api, extractError } from '../../lib/api';
import { useConfirm } from '../../lib/confirm';
import { fmtDate, fmtDateTime } from '../../lib/date';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { TYPE_BADGE, ABSENCE_TYPES, fmtDays } from './constants';

const WEEKDAYS = [
  { value: 0, label: 'Mo' }, { value: 1, label: 'Di' }, { value: 2, label: 'Mi' },
  { value: 3, label: 'Do' }, { value: 4, label: 'Fr' }, { value: 5, label: 'Sa' },
];

function periodRange(preset) {
  const now = new Date();
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (preset === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  if (preset === 'quarter') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(new Date(now.getFullYear(), now.getMonth() + 3, 0)) };
  if (preset === 'year') return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` };
  return { from: `${now.getFullYear() - 2}-01-01`, to: `${now.getFullYear() + 2}-12-31` };
}

export default function AbsenceList({ employees, canCreate, canEdit, canDelete, onOpenCreate, onOpenEdit, onOpenPatternCreate, onOpenPatternEdit, reloadKey }) {
  const [preset, setPreset] = useState('month');
  const [ranges, setRanges] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ employeeId: 'all', type: 'all', search: '', showPatterns: false });
  const { confirm } = useConfirm();

  const { from, to } = useMemo(() => periodRange(preset), [preset]);

  const load = async () => {
    setLoading(true);
    try {
      const [g, sp] = await Promise.all([
        api.get('/absences/grouped', { params: { from_date: from, to_date: to } }),
        api.get('/school-patterns'),
      ]);
      setRanges(g.data);
      setPatterns(sp.data);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, reloadKey]);

  const empMap = useMemo(() => Object.fromEntries(employees.map(e => [e.id, e])), [employees]);

  const filtered = useMemo(() => {
    return ranges
      .filter(r => filter.showPatterns ? true : r.source !== 'pattern')
      .filter(r => filter.employeeId === 'all' || r.employee_id === filter.employeeId)
      .filter(r => filter.type === 'all' || r.type === filter.type)
      .filter(r => {
        if (!filter.search) return true;
        const emp = empMap[r.employee_id];
        return emp && `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(filter.search.toLowerCase());
      })
      .sort((a, b) => b.start_date.localeCompare(a.start_date));
  }, [ranges, filter, empMap]);

  const patternsByEmployee = useMemo(() => {
    const m = {};
    for (const p of patterns) { if (filter.employeeId !== 'all' && p.employee_id !== filter.employeeId) continue; (m[p.employee_id] = m[p.employee_id] || []).push(p); }
    return m;
  }, [patterns, filter.employeeId]);

  const deletePatternSet = async (patternIds) => {
    if (!(await confirm({ title: 'Schulmuster löschen?', description: `Alle ${patternIds.length} Schulmuster dieses Mitarbeiters werden entfernt.`, variant: 'destructive', confirmLabel: 'Löschen' }))) return;
    try { for (const id of patternIds) await api.delete(`/school-patterns/${id}`); toast.success('Schulmuster gelöscht.'); load(); }
    catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-40 h-8 text-sm" data-testid="list-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Dieser Monat</SelectItem>
              <SelectItem value="quarter">Nächste 3 Monate</SelectItem>
              <SelectItem value="year">Dieses Jahr</SelectItem>
              <SelectItem value="all">Gesamter Zeitraum</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filter.search} onChange={e => setFilter({ ...filter, search: e.target.value })} placeholder="Name…" className="h-8 w-40 pl-7 text-sm" data-testid="list-search" />
          </div>
          <Select value={filter.employeeId} onValueChange={(v) => setFilter({ ...filter, employeeId: v })}>
            <SelectTrigger className="w-44 h-8 text-sm" data-testid="list-employee"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Mitarbeiter</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v })}>
            <SelectTrigger className="w-40 h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Arten</SelectItem>
              {ABSENCE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && <Button size="sm" variant="outline" onClick={onOpenPatternCreate} data-testid="list-add-pattern"><GraduationCap size={14} className="mr-1" />Schulmuster</Button>}
          {canCreate && <Button size="sm" onClick={() => onOpenCreate?.({})} data-testid="list-add-absence"><Plus size={14} className="mr-1" />Abwesenheit</Button>}
        </div>
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-list">Einträge ({filtered.length})</TabsTrigger>
          <TabsTrigger value="patterns" data-testid="tab-patterns">Schulmuster ({Object.keys(patternsByEmployee).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Zeitraum</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Art</TableHead>
                <TableHead>Startdatum</TableHead>
                <TableHead>Enddatum</TableHead>
                <TableHead className="text-right">Gesamtdauer</TableHead>
                <TableHead>Eingetragen am</TableHead>
                <TableHead>Angelegt von</TableHead>
                <TableHead className="text-right">Aktion</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((r, idx) => {
                  const emp = empMap[r.employee_id];
                  const isRange = r.start_date !== r.end_date;
                  const editable = r.source === 'manual';
                  return (
                    <TableRow key={idx} className={editable && canEdit ? 'cursor-pointer' : ''} onClick={() => editable && canEdit && onOpenEdit?.(r)} data-testid={`list-row-${idx}`}>
                      <TableCell className="font-mono text-xs whitespace-nowrap">{isRange ? `${fmtDate(r.start_date)}–${fmtDate(r.end_date)}` : fmtDate(r.start_date)}{r.half_day ? ' (½)' : ''}</TableCell>
                      <TableCell className="font-semibold whitespace-nowrap">{emp ? `${emp.first_name} ${emp.last_name}` : '—'}</TableCell>
                      <TableCell><Badge variant="outline" className={TYPE_BADGE[r.type] || ''}>{r.type}</Badge>{r.source === 'pattern' && <Badge variant="outline" className="ml-1 bg-violet-50 text-violet-700 border-violet-200"><GraduationCap size={10} className="mr-0.5" />Muster</Badge>}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(r.start_date)}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(r.end_date)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtDays(r.total_days ?? r.days_count)} T</TableCell>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">{r.created_at ? fmtDateTime(r.created_at) : '—'}</TableCell>
                      <TableCell className="text-xs text-slate-600 whitespace-nowrap">{r.created_by_name || '—'}</TableCell>
                      <TableCell className="text-right">
                        {editable && canEdit && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpenEdit?.(r); }} data-testid={`list-edit-${idx}`}><Edit2 size={14} /></Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Keine Abwesenheiten im Zeitraum.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="patterns">
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Mitarbeiter</TableHead>
                <TableHead>Wochentage</TableHead>
                <TableHead>Zeitraum</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {Object.entries(patternsByEmployee).map(([empId, pats]) => {
                  const emp = empMap[empId];
                  const weekdays = [...new Set(pats.map(p => p.weekday))].sort((a, b) => a - b);
                  const kwStart = Math.min(...pats.map(p => p.start_kw));
                  const kwEnd = Math.max(...pats.map(p => p.end_kw));
                  const year = pats[0]?.year;
                  return (
                    <TableRow key={empId} data-testid={`pattern-row-${empId}`}>
                      <TableCell className="font-semibold">{emp ? `${emp.first_name} ${emp.last_name}` : '—'}</TableCell>
                      <TableCell><div className="flex gap-1">{WEEKDAYS.map(w => <span key={w.value} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${weekdays.includes(w.value) ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-400'}`}>{w.label}</span>)}</div></TableCell>
                      <TableCell className="font-mono text-xs">KW{kwStart}–KW{kwEnd} / {year}</TableCell>
                      <TableCell>{pats.every(p => p.active) ? <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100 border-emerald-200">Aktiv</Badge> : <Badge variant="outline">Teilweise</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => onOpenPatternEdit?.({ employeeId: empId, existingPats: pats, weekdays: new Set(weekdays), kwStart, kwEnd, year, active: pats.every(p => p.active) })} data-testid={`pattern-edit-${empId}`}><Edit2 size={14} /></Button>
                        {canDelete && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deletePatternSet(pats.map(p => p.id))} data-testid={`pattern-delete-${empId}`}><Trash2 size={14} /></Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {Object.keys(patternsByEmployee).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Keine Schulmuster.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
